import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { api, isLoggedIn, trackEvent, thumbUrl } from '../api';
import { priceMain, priceApprox } from './price';
import { adPath } from './adUrl';
import { REGIONS, TASHKENT_DISTRICTS } from './RegionSelect';
import './home.css';
import { tenantText } from './TenantFilter';
import { SmartInput, parseSmart, pathFor } from './SmartSearch';
import heroImg from '../Images/home/hero-interior.webp';
import friendsImg from '../Images/home/friends-sofa.webp';
import catFlats from '../Images/home/cat-flats.webp';
import catRoommates from '../Images/home/cat-roommates.webp';
import catHouse from '../Images/home/cat-house.webp';
import catSeekers from '../Images/home/cat-seekers.webp';
import catStudents from '../Images/home/cat-students.webp';
import catCenter from '../Images/home/cat-center.webp';
import { loginUrl } from '../authNext';
import TelegramOffer from './TelegramOffer';

// ── Ikonkalar (inline SVG, stroke) ─────────────────────────────
const Svg = ({ size = 20, w = 1.9, children }) => (
  <svg viewBox='0 0 24 24' width={size} height={size} fill='none' stroke='currentColor' strokeWidth={w}
    strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>{children}</svg>
);
const I = {
  pin: (s) => <Svg size={s}><path d='M12 21s7-6.2 7-11.5A7 7 0 0 0 5 9.5C5 14.8 12 21 12 21z' /><circle cx='12' cy='9.5' r='2.5' /></Svg>,
  door: (s) => <Svg size={s}><path d='M4 21h16M6 21V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v17' /><circle cx='14.5' cy='12' r='.9' fill='currentColor' /></Svg>,
  wallet: (s) => <Svg size={s}><rect x='3' y='6' width='18' height='14' rx='3' /><path d='M3 10h18M16 15h2' /></Svg>,
  search: (s) => <Svg size={s} w={2.3}><circle cx='11' cy='11' r='7' /><path d='M20 20l-3.5-3.5' /></Svg>,
  shield: (s) => <Svg size={s}><path d='M12 3l7 3v5.5c0 4.4-3 8-7 9.5-4-1.5-7-5.1-7-9.5V6l7-3z' /><path d='M9 12l2 2 4-4' /></Svg>,
  percent: (s) => <Svg size={s}><circle cx='12' cy='12' r='9' /><path d='M9 15l6-6' /><circle cx='9.2' cy='9.2' r='.9' fill='currentColor' /><circle cx='14.8' cy='14.8' r='.9' fill='currentColor' /></Svg>,
  calendar: (s) => <Svg size={s}><rect x='3.5' y='5' width='17' height='15' rx='2.5' /><path d='M3.5 10h17M8 3v4M16 3v4' /><path d='M9.5 15l1.8 1.6 3.4-3.4' /></Svg>,
  bolt: (s) => <Svg size={s}><path d='M13 3L5 13.5h6L10 21l8-10.5h-6L13 3z' /></Svg>,
  map: (s) => <Svg size={s}><path d='M9 4L3.5 6v14L9 18l6 2 5.5-2V4L15 6 9 4z' /><path d='M9 4v14M15 6v14' /></Svg>,
  heart: (s) => <Svg size={s}><path d='M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z' /></Svg>,
  heartFill: (s) => <Svg size={s}><path fill='currentColor' d='M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z' /></Svg>,
  home: (s) => <Svg size={s}><path d='M4 10.5L12 4l8 6.5V20a1 1 0 0 1-1 1h-4.5v-6h-5v6H5a1 1 0 0 1-1-1v-9.5z' /></Svg>,
  area: (s) => <Svg size={s}><rect x='4' y='4' width='16' height='16' rx='2' /><path d='M4 9h3M4 14h2M9 4v3M14 4v2' /></Svg>,
  layers: (s) => <Svg size={s}><path d='M12 4l8 4-8 4-8-4 8-4z' /><path d='M4 12l8 4 8-4M4 16l8 4 8-4' /></Svg>,
  arrowL: (s) => <Svg size={s} w={2.2}><path d='M15 6l-6 6 6 6' /></Svg>,
  arrowR: (s) => <Svg size={s} w={2.2}><path d='M9 6l6 6-6 6' /></Svg>,
  users: (s) => <Svg size={s}><circle cx='9' cy='8.5' r='3.2' /><path d='M3.5 19.5c.6-3.2 2.8-5 5.5-5s4.9 1.8 5.5 5' /><circle cx='16.5' cy='9.5' r='2.6' /><path d='M15.8 14.6c2.4.1 4.1 1.7 4.7 4.4' /></Svg>,
};

// ── 1–2. Qidiruv: bitta holat — karta filtrlari pastdagi e'lonlarni darhol o'zgartiradi ──
// Ikki bo'lim: «Ijaraga uy» (butun uy, oylik narx) va «Xonadosh topish» (sheriklikka joy, narx 1 kishiga).
const KINDS = {
  rent: {
    tab: 'Ijaraga uy', title: 'Ijaraga uylar', path: '/allrentadvertising',
    examples: ['oilaga 3 xonali kvartira 6 mln gacha', '2 ta yigit talabaga Chilonzorda', 'Yunusobodda arzon kvartira'],
    price: [['', 'Narxi'], ['2000000', '2 mln gacha'], ['4000000', '4 mln gacha'], ['6000000', '6 mln gacha'], ['8000000', '8 mln gacha']],
    who: [['', 'Kimga'], ['FAMILY', 'Oilaga'], ['STUDENT_MALE', 'Talaba yigitlarga'], ['STUDENT_FEMALE', 'Talaba qizlarga']],
  },
  partner: {
    tab: 'Xonadosh topish', title: "Xonadosh e'lonlari", path: '/allpatneradvertising',
    examples: ['Chilonzorda qizlarga joy', 'talaba yigitga 1 mln gacha', 'Yunusobodda sheriklikka kvartira'],
    // Sheriklikda narx bir kishiniki (prod: o'rtacha ~1,1 mln) — oraliqlar shunga mos
    price: [['', 'Narxi'], ['800000', '800 ming gacha'], ['1000000', '1 mln gacha'], ['1500000', '1,5 mln gacha'], ['2000000', '2 mln gacha']],
    who: [['', 'Kimga'], ['FEMALE', 'Qizlarga'], ['MALE', 'Yigitlarga']],
  },
};
const TYPE_OPTS = [['', 'Turi'], ['FLAT', 'Kvartira'], ['YARD', 'Hovli']];
const ROOM_OPTS = [['', 'Xonalar'], ['1', '1 xonali'], ['2', '2 xonali'], ['3', '3 xonali'], ['4+', '4 va undan ko\'p']];
const EMPTY = { loc: 'Toshkent shahri|', type: '', rooms: '', price: '', who: '' };

/** Holat → qidiruv parametrlari (ro'yxat sahifasi URL'i va API uchun bir xil nomlar) */
function filterParams(tab, f) {
  const [region, district] = f.loc.split('|');
  const p = { region, district, maxPrice: f.price };
  if (tab === 'rent') {
    p.accommodationType = f.type;
    if (f.rooms === '4+') p.minRoomCount = '4'; else p.roomCount = f.rooms;
    p.tenant = f.who;
  } else {
    p.targetAudience = f.who;
  }
  return Object.fromEntries(Object.entries(p).filter(([, v]) => v));
}

/** Bosh sahifaning yuqori qismi: qidiruv kartasi + shu filtrlar bo'yicha e'lonlar */
export function HomeFinder() {
  const [tab, setTab] = useState('rent');
  const [f, setF] = useState(EMPTY);
  const [total, setTotal] = useState(null);
  const switchTab = (k) => {
    if (k === tab) return;
    setTab(k);
    setF(prev => ({ ...EMPTY, loc: prev.loc })); // hudud saqlanadi; narx/kimga oraliqlari bo'limga xos
    trackEvent('home_filter', `tab=${k}`);
  };
  const change = (key, value) => {
    setF(prev => ({ ...prev, [key]: value }));
    trackEvent('home_filter', `${key}=${key === 'loc' ? 'set' : value || 'any'}`);
  };
  return (<>
    <HomeHero compact tab={tab} f={f} total={total} onTab={switchTab} onChange={change} />
    <HomeFeatured tab={tab} f={f} onTotal={setTotal} onReset={() => setF(prev => ({ ...EMPTY, loc: prev.loc }))} />
  </>);
}

function Chip({ icon, value, onChange, label, children }) {
  return (
    <label className={`hm-chip${value ? ' on' : ''}`}>
      {icon}
      <select value={value} onChange={e => onChange(e.target.value)} aria-label={label}>{children}</select>
    </label>
  );
}

export default function HomeHero({ compact = false, tab = 'rent', f = EMPTY, total = null, onTab, onChange }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const kind = KINDS[tab];

  const go = async () => {
    // Matn yozilgan bo'lsa — u ustun: «2 ta yigit talabaga Chilonzorda» → filtrlar (server tushunadi)
    if (text.trim()) {
      const s = await parseSmart(text, tab === 'partner' ? 'PARTNER' : 'RENT');
      if (s) {
        trackEvent('search', `text:${[...s.params.keys()].filter(k => k !== 'q').join(',')}`);
        navigate(`${pathFor(s.kind)}?${s.params}`);
        return;
      }
    }
    const q = new URLSearchParams(filterParams(tab, f));
    trackEvent('search', `chips:${tab}:${[...q.keys()].join(',')}`);
    navigate(`${kind.path}${q.toString() ? '?' + q : ''}`);
  };

  const trust = [
    [I.shield, "Shikoyat qilish oson"],
    [I.percent, 'Moslik foizi'],
    [I.calendar, "Onlayn ko'rish"],
    [I.bolt, 'Tez va qulay'],
  ];

  return (
    <section className={`hm-hero${compact ? ' compact' : ''}`} aria-labelledby='hm-hero-title'>
      <div className='hm-hero-top'>
        {!compact && <div className='hm-hero-media'>
          <img className='hm-hero-img' src={heroImg} alt={t("Yorug' va zamonaviy yashash xonasi")} fetchPriority='high' />
          <figure className='hm-polaroid'>
            <img src={friendsImg} alt={t('Bir stol atrofida suhbatlashayotgan yoshlar')} />
            <figcaption>{I.users(15)}{t("Yangi do'stlar, yangi imkoniyatlar")}</figcaption>
          </figure>
        </div>}

        <div className='hm-wrap hm-hero-inner'>
          <div className='hm-hero-copy'>
            {!compact && <span className='hm-eyebrow'>{I.pin(15)}{t("O'zbekiston bo'ylab")}</span>}
            <h1 id='hm-hero-title'>
              <span>{t('Uy va xonadoshni')}</span>
              <span><Trans i18nKey='<em>bir joyda</em> toping' components={{ em: <em /> }} /></span>
            </h1>
            {!compact && <p className='hm-lead'>{t("Ijaraga uy, bo'sh xona yoki o'zingizga mos sherik — moslik foizi, narx bahosi va ko'rishga onlayn yozilish bilan.")}</p>}
            {!compact && <ul className='hm-trust'>
              {trust.map(([ic, l]) => <li key={l}><span className='hm-trust-ic'>{ic(16)}</span>{t(l)}</li>)}
            </ul>}
          </div>
        </div>
      </div>

      <div className='hm-wrap'>
        <form className='hm-search' role='search' aria-label={t('Qidirish')} onSubmit={e => { e.preventDefault(); go(); }}>
          <div className='hm-tabs' role='tablist' aria-label={t('Nima qidiryapsiz?')}>
            {Object.entries(KINDS).map(([k, v]) => (
              <button key={k} type='button' role='tab' aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => onTab?.(k)}>
                {k === 'rent' ? I.home(17) : I.users(17)}{t(v.tab)}
              </button>
            ))}
          </div>
          <p className='hm-tab-hint'>{tab === 'rent'
            ? t("Butun kvartira yoki hovli — oylik narx")
            : t("Sheriklikka joy yoki xona — narx 1 kishiga")}</p>

          <SmartInput value={text} onChange={setText} examples={kind.examples} key={tab} />

          <div className='hm-chips' role='group' aria-label={t('Filtrlar')}>
            <Chip icon={I.pin(16)} value={f.loc === 'Toshkent shahri|' ? '' : f.loc} label={t('Qayerda?')}
              onChange={v => onChange?.('loc', v || 'Toshkent shahri|')}>
              <option value=''>{t('Toshkent shahri')}</option>
              <optgroup label='Toshkent shahri'>
                {TASHKENT_DISTRICTS.map(d => <option key={d} value={`Toshkent shahri|${d}`}>{d}</option>)}
              </optgroup>
              <optgroup label={t('Viloyatlar')}>
                {REGIONS.filter(r => r !== 'Toshkent shahri').map(r => <option key={r} value={`${r}|`}>{r}</option>)}
              </optgroup>
              <option value='|'>{t("Butun O'zbekiston")}</option>
            </Chip>
            {tab === 'rent' && <Chip icon={I.home(16)} value={f.type} label={t('Uy turi')} onChange={v => onChange?.('type', v)}>
              {TYPE_OPTS.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}
            </Chip>}
            {tab === 'rent' && <Chip icon={I.door(16)} value={f.rooms} label={t('Xonalar')} onChange={v => onChange?.('rooms', v)}>
              {ROOM_OPTS.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}
            </Chip>}
            <Chip icon={I.wallet(16)} value={f.price} label={t('Narxi')} onChange={v => onChange?.('price', v)}>
              {kind.price.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}
            </Chip>
            <Chip icon={I.users(16)} value={f.who} label={t('Kimga')} onChange={v => onChange?.('who', v)}>
              {kind.who.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}
            </Chip>
          </div>

          <div className='hm-go-row'>
            <button type='submit' className='hm-go'>
              {I.search(20)}{t('Qidirish')}
              {total != null && <span className='hm-go-count'>{t("{{n}} ta e'lon", { n: total })}</span>}
            </button>
            <Link className='hm-owner-link' to='/wanted'>{t('Uy egasimisiz? Uy izlayotganlarni ko\'ring')} {I.arrowR(15)}</Link>
          </div>
        </form>
      </div>
    </section>
  );
}

// ── 3. Nima qidiryapsiz? ───────────────────────────────────────
const CATS = [
  [catFlats, 'Kvartiralar', '/allrentadvertising?accommodationType=FLAT'],
  [catRoommates, 'Xonadoshlar', '/allpatneradvertising'],
  [catHouse, 'Hovli uylar', '/allrentadvertising?accommodationType=YARD'],
  [catSeekers, 'Uy qidiruvchilar', '/wanted'],
  [catStudents, 'Talabalar uchun', '/allpatneradvertising'],
  [catCenter, 'Toshkent markazi', `/allrentadvertising?${new URLSearchParams({ region: 'Toshkent shahri', district: 'Mirobod' })}`],
];

export function HomeCategories() {
  const { t } = useTranslation();
  return (
    <section className='hm-sec hm-wrap' aria-labelledby='hm-cats-title'>
      <div className='hm-head'>
        <h2 id='hm-cats-title'>{t('Nima qidiryapsiz?')}</h2>
        <Link className='hm-more' to='/allrentadvertising'>{t("Barchasini ko'rish")}<span aria-hidden='true'>{I.arrowR(16)}</span></Link>
      </div>
      <ul className='hm-cats'>
        {CATS.map(([img, label, to]) => (
          <li key={label}>
            <Link className='hm-cat' to={to}>
              <span className='hm-cat-img'><img src={img} alt='' loading='lazy' /></span>
              <span className='hm-cat-lbl'>{t(label)}<span className='hm-cat-arr' aria-hidden='true'>{I.arrowR(15)}</span></span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ── 4. Asosiy afzalliklarimiz ──────────────────────────────────
const FEATURES = [
  [I.home, "Haqiqiy e'lonlar", "E'lonlarni uy egalari va sheriklar o'zlari joylaydi. Shubhali e'lon haqida shikoyat qilish mumkin."],
  [I.percent, 'Moslik foizi', "Anketangiz asosida har bir e'lon sizga necha foiz mos kelishini ko'rasiz."],
  [I.calendar, "Onlayn ko'rishga yozilish", "Qulay vaqtni tanlab, uyni ko'rishga so'rov yuborasiz — egasi tasdiqlaydi."],
  [I.map, 'Xarita orqali qidirish', "E'lonlarni xaritada ko'ring va o'zingizga qulay hududni tanlang."],
  [I.heart, "Sevimlilar ro'yxati", "Yoqqan e'lonlarni saqlang, yangi mos e'lonlarni Telegram'da oling."],
];

export function HomeFeatures() {
  const { t } = useTranslation();
  return (
    <section className='hm-sec hm-wrap' aria-labelledby='hm-feat-title'>
      <div className='hm-feats-box'>
        <div className='hm-head'><h2 id='hm-feat-title'>{t('Asosiy afzalliklarimiz')}</h2></div>
        <ul className='hm-feats'>
          {FEATURES.map(([ic, title, d]) => (
            <li key={title} className='hm-feat'>
              <span className='hm-feat-ic'>{ic(24)}</span>
              <b>{t(title)}</b>
              <p>{t(d)}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ── 5. Tavsiya etilgan e'lonlar ────────────────────────────────
const TYPES = { YARD: 'Hovli', FLAT: 'Kvartira' };

function ListingCard({ ad, partner, saved, onSave, tenantActive }) {
  const { t } = useTranslation();
  const img = ad.attachmentsDto?.[0]?.fileName;
  const type = TYPES[ad.accommodationType] ? t(TYPES[ad.accommodationType]) : ad.accommodationType;
  const title = ad.title || t('{{type}}, {{n}} xonali', { type, n: ad.roomCount || '?' });
  const place = [...new Set([ad.district, ad.region].filter(Boolean))].join(', ');
  const url = adPath(ad, partner);
  const floor = ad.floor && (ad.totalFloors ? t('{{floor}}/{{total}} qavat', { floor: ad.floor, total: ad.totalFloors }) : t('{{floor}}-qavat', { floor: ad.floor }));
  return (
    <article className='hm-card'>
      <div className='hm-card-img'>
        {img ? <img src={thumbUrl(img)} alt='' loading='lazy' /> : <span className='hm-card-noimg'>{I.home(30)}</span>}
        <span className='hm-card-badges'>
          {ad.promoted && <span className='hm-badge top'>TOP</span>}
          <span className='hm-badge'>{partner ? `${t('Xonadosh')} · ` : ''}{type}</span>
        </span>
        <button type='button' className={`hm-save${saved ? ' on' : ''}`} aria-pressed={!!saved}
          aria-label={saved ? t('Saqlangan') : t('Saqlash')} onClick={() => onSave(ad.id)}>
          {saved ? I.heartFill(19) : I.heart(19)}
        </button>
      </div>
      <div className='hm-card-body'>
        <div className='hm-card-price'>
          <b>{priceMain(ad)}</b><span>{partner ? t('/oy · 1 kishi') : t('/ oy')}</span>
        </div>
        {priceApprox(ad) && <div className='hm-card-approx'>{priceApprox(ad)}</div>}
        {place && <p className='hm-card-place'>{I.pin(14)}<span>{place}</span></p>}
        <h3 className='hm-card-title'><Link to={url}>{title}</Link></h3>
        <ul className='hm-card-specs'>
          {ad.roomCount ? <li>{I.door(15)}{t('{{n}} xona', { n: ad.roomCount })}</li> : null}
          {ad.roomVolume ? <li>{I.area(15)}{ad.roomVolume} m²</li> : null}
          {floor ? <li>{I.layers(15)}{floor}</li> : null}
        </ul>
        {!partner && (tenantText(ad.tenantTypes, t)
          ? <p className='hm-card-tenant'>{I.users(14)}<span>{tenantText(ad.tenantTypes, t)}{ad.maxTenants ? ` · ${t('{{n}} kishigacha', { n: ad.maxTenants })}` : ''}</span></p>
          : tenantActive && <p className='hm-card-tenant unknown'>{t("Kimlarga — egasi ko'rsatmagan")}</p>)}
      </div>
    </article>
  );
}

export function HomeFeatured({ tab = 'rent', f = EMPTY, onTotal, onReset }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ads, setAds] = useState(null);
  const [fav, setFav] = useState({ ads: new Set(), partner: new Set() });
  const partner = tab === 'partner';
  const params = filterParams(tab, f);
  const key = JSON.stringify([tab, params]);

  useEffect(() => {
    let live = true;
    setAds(null);
    (partner ? api.getAllPartnerAds({ page: 0, size: 12, status: 'A', ...params })
      : api.getAllAds({ page: 0, size: 12, status: 'A', ...params }))
      .then(res => {
        if (!live) return;
        const el = res?.element;
        setAds(el?.content || (Array.isArray(el) ? el : []));
        onTotal?.(typeof el?.totalElements === 'number' ? el.totalElements : null);
      })
      .catch(() => { if (live) { setAds([]); onTotal?.(null); } });
    return () => { live = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isLoggedIn()) return;
    Promise.all([api.getFavoriteAds().catch(() => null), api.getFavoritePartnerAds().catch(() => null)]).then(([a, b]) => setFav({
      ads: new Set((a?.element?.content || []).map(x => x.id)),
      partner: new Set((b?.element?.content || []).map(x => x.id)),
    }));
  }, []);

  const save = useCallback(async (id) => {
    if (!isLoggedIn()) { navigate(loginUrl(window.location.pathname + window.location.search)); return; }
    const res = await (partner ? api.toggleFavoritePartnerAd(id) : api.toggleFavoriteAd(id));
    if (res?.element !== 'added' && res?.element !== 'removed') return;
    setFav(prev => {
      const k = partner ? 'partner' : 'ads';
      const next = new Set(prev[k]);
      if (res.element === 'added') next.add(id); else next.delete(id);
      return { ...prev, [k]: next };
    });
  }, [partner, navigate]);

  const savedSet = partner ? fav.partner : fav.ads;
  const q = new URLSearchParams(params).toString();
  const allUrl = `${KINDS[tab].path}${q ? '?' + q : ''}`;
  const filtered = Object.keys(params).some(k => k !== 'region') || params.region !== 'Toshkent shahri';
  const tgFilters = {
    adType: partner ? 'PARTNER' : 'RENT', region: params.region || null, district: params.district || null,
    maxPrice: f.price ? +f.price : null, rooms: partner ? null : f.rooms === '4+' ? 4 : f.rooms ? +f.rooms : null,
  };
  return (
    <section className='hm-sec hm-wrap' aria-labelledby='hm-top-title' aria-live='polite'>
      <div className='hm-head'>
        <h2 id='hm-top-title'>{t(KINDS[tab].title)}</h2>
        <Link className='hm-more' to={allUrl}>{t('Hammasi')}<span aria-hidden='true'>{I.arrowR(16)}</span></Link>
      </div>

      {ads === null ? (
        <ul className='hm-track hm-grid' aria-busy='true'>
          {[0, 1, 2, 3, 4, 5, 6, 7].map(i => <li key={i}><div className='hm-card hm-skel'><div className='hm-card-img' /><div className='hm-card-body'><i /><i /><i /></div></div></li>)}
        </ul>
      ) : ads.length === 0 ? (
        <div className='hm-empty'>
          <span className='hm-feat-ic'>{I.home(24)}</span>
          <div>
            <b>{filtered ? t("Bu filtrlar bo'yicha e'lon topilmadi") : t("Hozircha bu bo'limda e'lon yo'q")}</b>
            <p>{filtered ? t("Narx yoki hududni kengaytirib ko'ring.") : t("Birinchi bo'lib e'lon joylang.")}</p>
          </div>
          {filtered && <button type='button' className='hm-btn-ghost' onClick={onReset}>{t('Filtrlarni tozalash')}</button>}
        </div>
      ) : (
        <ul className='hm-track hm-grid' key={key}>
          {ads.map(ad => (
            <li key={ad.id}><ListingCard ad={ad} partner={partner} saved={savedSet.has(ad.id)} onSave={save} tenantActive={!!(tab === 'rent' && f.who)} /></li>
          ))}
        </ul>
      )}
      {ads && ads.length > 0 && (
        <div className='hm-all'>
          <Link className='hm-btn-ghost' to={allUrl}>{t("Barcha e'lonlarni ko'rish")}</Link>
        </div>
      )}
      {/* Bugun topmasa ham qaytsin: shu filtrlar bo'yicha yangi e'lonlar Telegram'ga */}
      {ads && (
        <TelegramOffer place={ads.length ? 'home' : 'home_empty'} filters={tgFilters} text={ads.length
          ? t("Bu qidiruv bo'yicha yangi e'lonlar chiqishi bilan Telegram'ga yuboramiz. Ro'yxatdan o'tish shart emas.")
          : t("Hozircha mos e'lon yo'q — yangisi chiqishi bilan Telegram'ga yuboramiz. Ro'yxatdan o'tish shart emas.")} />
      )}
    </section>
  );
}

// ── Qanday ishlaydi + CTA ──────────────────────────────────────
export function HowItWorks() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const steps = [
    ['1', 'Anketani to\'ldiring', "Byudjet, hudud va turmush tarzingizni belgilang — 2 daqiqa."],
    ['2', 'Mos variantlarni ko\'ring', "Har bir e'londa sizga necha foiz mosligi va bozor narxi bilan solishtiruv."],
    ['3', "Ko'rishga yoziling", "Qulay vaqtni tanlang, egasi tasdiqlaydi — uyni borib ko'rasiz."],
  ];
  return (
    <section className='hiw'>
      <div className='hiw-head'>
        <h2>{t('Qanday ishlaydi')}</h2>
        <p>{t('Uch qadamda uy yoki sherik toping')}</p>
      </div>
      <div className='hiw-grid'>
        {steps.map(([n, title, d]) => (
          <div className='hiw-card' key={n}><span>{n}</span><b>{t(title)}</b><p>{t(d)}</p></div>
        ))}
      </div>
      <div className='hiw-cta'>
        <div>
          <b>{t("Uyingiz yoki bo'sh xonangiz bormi?")}</b>
          <p>{t("E'lon joylash bepul. Mos ijarachilarga Telegram orqali darhol yetib boradi.")}</p>
        </div>
        <button type='button' onClick={() => navigate(isLoggedIn() ? '/profile/elonlar' : '/registr')}>{t("+ E'lon joylash")}</button>
      </div>
    </section>
  );
}
